import { useEffect, useState } from "react";
import { Button, Card, Col, Container, Form, Nav, Row } from "react-bootstrap";
import { BsPlusLg } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import i18n from "../../../i18n";
import {
    createOrganizationSettings,
    fetchOrganizationSettings,
    updateOrganizationSettings,
} from "../../../redux/slices/organizationSettingsSlice";
import "./OrganizationSettings.css";
import OfficeLocationsCard from "./OfficeLocationsCard";
import ManagePermissions from "../../permissions/ManagePermissions/ManagePermissions";
import OrganizationDocuments from "../OrganizationDocuments/OrganizationDocuments";
import { ROLES } from "../../../utils/constants/roles.js";
import {
    COMPENSATION_TYPE,
    CURRENCY,
    DEFAULT_CURRENCIES,
    TIME_ZONE,
    DEFAULT_TIME_ZONES,
    DEFAULT_ABSENCE_TYPES,
    DEFAULT_LANGUAGES,
    DEFAULT_NOTIFICATION_METHODS
} from "../../../utils/constants/organization.js";

function getEmptySettings() {
    return {
        organization_name: "",
        working_hours_per_day: 8.0,
        default_time_zone: TIME_ZONE.UTC,
        default_currency: CURRENCY.EUR,
        default_absence_types: [...DEFAULT_ABSENCE_TYPES],
        default_languages: [...DEFAULT_LANGUAGES],
        absence_approval_required: true,
        shift_change_approval_required: true,
        max_overtime_hours_per_week: 20.0,
        max_overtime_hours_per_month: 80.0,
        max_overtime_hours_per_year: 200.0,
        overtime_calculation_method: "daily",
        compensation_type: COMPENSATION_TYPE.TIME,
        notifications_enabled: true,
        notification_methods: [...DEFAULT_NOTIFICATION_METHODS],
        default_project_tags: [],
        default_event_tags: [],
        default_work_item_tags: [],
        default_document_tags: [],
        default_delegation_tags: [],
        allowed_auth_methods: ["CREDENTIALS"],
        sso_issuer_url: "",
        sso_client_id: "",
        sso_client_secret: ""
    };
}

const OrganizationSettings = () => {
    const dispatch = useDispatch();
    const settings = useSelector((state) => state.organization.item);
    const isLoading = useSelector((state) => state.organization.isLoading);
    const error = useSelector((state) => state.organization.error);
    const user = useSelector((state) => state.auth.user);
    const location = useLocation();
    const isPermissionsTab = location.pathname === "/organization/settings/permissions";
    const isDocumentsTab = location.pathname === "/organization/settings/documents";
    const isSuperAdmin = user?.role?.name === ROLES.SUPER_ADMIN;

    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState(getEmptySettings());
    const [absenceTypeInput, setAbsenceTypeInput] = useState("");
    const [languageInput, setLanguageInput] = useState("");
    const [notificationMethodInput, setNotificationMethodInput] = useState("");
    const [tagInputs, setTagInputs] = useState({
        project: "", event: "", workItem: "", document: "", delegation: ""
    });

    useEffect(() => {
        dispatch(fetchOrganizationSettings());
    }, [dispatch]);

    useEffect(() => {
        if (settings) {
            setFormData({
                organization_name: settings.organization_name || "",
                working_hours_per_day: settings.working_hours_per_day ?? 8.0,
                default_time_zone: settings.default_time_zone || TIME_ZONE.UTC,
                default_currency: settings.default_currency || CURRENCY.EUR,
                default_absence_types: settings.default_absence_types ? [...settings.default_absence_types] : [...DEFAULT_ABSENCE_TYPES],
                default_languages: settings.default_languages ? [...settings.default_languages] : [...DEFAULT_LANGUAGES],
                absence_approval_required: settings.absence_approval_required ?? true,
                shift_change_approval_required: settings.shift_change_approval_required ?? true,
                max_overtime_hours_per_week: settings.max_overtime_hours_per_week ?? 20.0,
                max_overtime_hours_per_month: settings.max_overtime_hours_per_month ?? 80.0,
                max_overtime_hours_per_year: settings.max_overtime_hours_per_year ?? 200.0,
                overtime_calculation_method: settings.overtime_calculation_method || "daily",
                compensation_type: settings.compensation_type || COMPENSATION_TYPE.TIME,
                notifications_enabled: settings.notifications_enabled ?? true,
                notification_methods: settings.notification_methods ? [...settings.notification_methods] : [...DEFAULT_NOTIFICATION_METHODS],
                default_project_tags: settings.default_project_tags ? [...settings.default_project_tags] : [],
                default_event_tags: settings.default_event_tags ? [...settings.default_event_tags] : [],
                default_work_item_tags: settings.default_work_item_tags ? [...settings.default_work_item_tags] : [],
                default_document_tags: settings.default_document_tags ? [...settings.default_document_tags] : [],
                default_delegation_tags: settings.default_delegation_tags ? [...settings.default_delegation_tags] : [],
                allowed_auth_methods: settings.allowed_auth_methods ? [...settings.allowed_auth_methods] : ["CREDENTIALS"],
                sso_issuer_url: settings.sso_issuer_url || "",
                sso_client_id: settings.sso_client_id || "",
                sso_client_secret: settings.sso_client_secret || "",
            });
        } else if (!isLoading && !settings) {
            setFormData(getEmptySettings());
        }
    }, [settings, isLoading]);

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleAuthMethodToggle = (method) => {
        setFormData((prev) => {
            const methods = prev.allowed_auth_methods || [];
            if (methods.includes(method)) {
                if (methods.length === 1) return prev;
                return { ...prev, allowed_auth_methods: methods.filter((m) => m !== method) };
            } else {
                return { ...prev, allowed_auth_methods: [...methods, method] };
            }
        });
    };

    const addAbsenceType = () => {
        if (!absenceTypeInput.trim()) return;
        const trimmed = absenceTypeInput.trim();
        if (formData.default_absence_types.includes(trimmed)) return;
        setFormData((prev) => ({ ...prev, default_absence_types: [...prev.default_absence_types, trimmed] }));
        setAbsenceTypeInput("");
    };

    const removeAbsenceType = (type) => {
        setFormData((prev) => ({ ...prev, default_absence_types: prev.default_absence_types.filter((t) => t !== type) }));
    };

    const addLanguage = () => {
        if (!languageInput.trim()) return;
        const trimmed = languageInput.trim().toLowerCase();
        if (formData.default_languages.includes(trimmed)) return;
        setFormData((prev) => ({ ...prev, default_languages: [...prev.default_languages, trimmed] }));
        setLanguageInput("");
    };

    const removeLanguage = (lang) => {
        setFormData((prev) => ({ ...prev, default_languages: prev.default_languages.filter((l) => l !== lang) }));
    };

    const addNotificationMethod = () => {
        if (!notificationMethodInput.trim()) return;
        const trimmed = notificationMethodInput.trim().toLowerCase();
        if (formData.notification_methods.includes(trimmed)) return;
        setFormData((prev) => ({ ...prev, notification_methods: [...prev.notification_methods, trimmed] }));
        setNotificationMethodInput("");
    };

    const removeNotificationMethod = (method) => {
        setFormData((prev) => ({ ...prev, notification_methods: prev.notification_methods.filter((m) => m !== method) }));
    };

    const addTag = (category) => {
        if (!tagInputs[category]?.trim()) return;
        const trimmed = tagInputs[category].trim();
        setFormData((prev) => ({
            ...prev,
            [`default_${category}_tags`]: [...(prev[`default_${category}_tags`] || []), trimmed]
        }));
        setTagInputs((prev) => ({ ...prev, [category]: "" }));
    };

    const removeTag = (category, tag) => {
        setFormData((prev) => ({
            ...prev,
            [`default_${category}_tags`]: prev[`default_${category}_tags`].filter((t) => t !== tag)
        }));
    };

    const handleSave = () => {
        if (settings?.id) {
            dispatch(updateOrganizationSettings(formData));
        } else {
            dispatch(createOrganizationSettings(formData));
        }
        setIsEditing(false);
    };

    const renderTagsInput = (category, label) => (
        <div className="mb-3">
            <Form.Label>{label}</Form.Label>
            <div className="d-flex gap-2">
                <Form.Control
                    type="text"
                    value={tagInputs[category]}
                    onChange={(e) => setTagInputs((prev) => ({ ...prev, [category]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(category); } }}
                />
                <Button variant="outline-secondary" size="sm" onClick={() => addTag(category)}>
                    <BsPlusLg size={14} />
                </Button>
            </div>
            <div className="d-flex flex-wrap gap-2 mt-2">
                {(formData[`default_${category}_tags`] || []).map((tag) => (
                    <span key={tag} className="badge bg-light text-dark d-inline-flex align-items-center gap-1 border">
                        {tag}
                        {isEditing && (
                            <button type="button" className="btn-close btn-sm p-0 ms-1" onClick={() => removeTag(category, tag)} />
                        )}
                    </span>
                ))}
            </div>
        </div>
    );

    if (isLoading && !settings) {
        return (
            <Container className="py-4">
                <div className="d-flex justify-content-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">{i18n.t("common.loading")}</span>
                    </div>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid className="organization-settings-page">
            <Container className="organization-settings-shell my-5 py-4 px-3 px-md-4 px-lg-5 fade-in-up">
                <div className="organization-settings-toolbar mb-4 d-flex justify-content-between align-items-start flex-wrap gap-3">
                    <div>
                        <h2 className="mb-1">{i18n.t("pages.OrganizationSettings.title")}</h2>
                        <p className="text-muted mb-0">
                            {settings?.id ? i18n.t("pages.OrganizationSettings.subtitle_exists") : i18n.t("pages.OrganizationSettings.subtitle_new")}
                        </p>
                    </div>
                    {!isPermissionsTab && !isDocumentsTab && !isEditing && (
                        <Button variant="primary" onClick={() => setIsEditing(true)} className="organization-cta">
                            {settings?.id ? i18n.t("pages.OrganizationSettings.edit") : i18n.t("pages.OrganizationSettings.create")}
                        </Button>
                    )}
                </div>

                {isSuperAdmin && (
                    <Nav variant="tabs" className="mb-4">
                        <Nav.Item>
                            <NavLink to="/organization/settings" end className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>{i18n.t("pages.OrganizationSettings.tab_general")}</NavLink>
                        </Nav.Item>
                        <Nav.Item>
                            <NavLink to="/organization/settings/permissions" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>{i18n.t("pages.OrganizationSettings.tab_permissions")}</NavLink>
                        </Nav.Item>
                        <Nav.Item>
                            <NavLink to="/organization/settings/documents" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>{i18n.t("pages.OrganizationSettings.tab_documents")}</NavLink>
                        </Nav.Item>
                    </Nav>
                )}

                {isPermissionsTab ? (
                    <ManagePermissions />
                ) : isDocumentsTab ? (
                    <OrganizationDocuments />
                ) : (
                <>
                {(error && settings) && (
                    <Card className="border-danger mb-4">
                        <Card.Body>
                            <p className="text-danger mb-0">{i18n.t("pages.OrganizationSettings.error")}</p>
                        </Card.Body>
                    </Card>
                )}

                <Card className="shadow-sm border-0 rounded-4 mb-4">
                    <Card.Header className="border-bottom bg-body fw-semibold px-4 py-3">{i18n.t("pages.OrganizationSettings.general_title")}</Card.Header>
                    <Card.Body className="px-4 py-4">
                        <Form>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>{i18n.t("pages.OrganizationSettings.organization_name")}</Form.Label>
                                        <Form.Control type="text" value={formData.organization_name} onChange={(e) => handleInputChange("organization_name", e.target.value)} disabled={!isEditing} />
                                    </Form.Group>
                                </Col>

                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>{i18n.t("pages.OrganizationSettings.working_hours_per_day")}</Form.Label>
                                        <Form.Control type="number" step="0.5" value={formData.working_hours_per_day} onChange={(e) => handleInputChange("working_hours_per_day", parseFloat(e.target.value))} disabled={!isEditing} />
                                    </Form.Group>
                                </Col>

                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>{i18n.t("pages.OrganizationSettings.default_time_zone")}</Form.Label>
                                        <Form.Select value={formData.default_time_zone} onChange={(e) => handleInputChange("default_time_zone", e.target.value)} disabled={!isEditing}>
                                            {DEFAULT_TIME_ZONES.map((tz) => (<option key={tz} value={tz}>{tz}</option>))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row className="mb-3">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>{i18n.t("pages.OrganizationSettings.default_currency")}</Form.Label>
                                        <Form.Select value={formData.default_currency} onChange={(e) => handleInputChange("default_currency", e.target.value)} disabled={!isEditing}>
                                            {DEFAULT_CURRENCIES.map((cur) => (<option key={cur} value={cur}>{cur}</option>))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>{i18n.t("pages.OrganizationSettings.absence_approval_required")}</Form.Label>
                                        <Form.Check type="switch" checked={formData.absence_approval_required} onChange={(e) => handleInputChange("absence_approval_required", e.target.checked)} disabled={!isEditing} />
                                    </Form.Group>
                                </Col>

                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>{i18n.t("pages.OrganizationSettings.shift_change_approval_required")}</Form.Label>
                                        <Form.Check type="switch" checked={formData.shift_change_approval_required} onChange={(e) => handleInputChange("shift_change_approval_required", e.target.checked)} disabled={!isEditing} />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Form>
                    </Card.Body>
                </Card>

                <OfficeLocationsCard />

                <Card className="shadow-sm border-0 rounded-4 mb-4">
                    <Card.Header className="border-bottom bg-body fw-semibold px-4 py-3">
                        {i18n.t("pages.OrganizationSettings.authentication_title", "Authentication Settings")}
                    </Card.Header>
                    <Card.Body className="px-4 py-4">
                        <Form>
                            <Form.Group className="mb-4">
                                <Form.Label className="fw-semibold d-block">
                                    {i18n.t("pages.OrganizationSettings.allowed_auth_methods", "Allowed Authentication Methods")}
                                </Form.Label>
                                <Form.Check
                                    inline
                                    type="checkbox"
                                    id="auth-credentials"
                                    label={i18n.t("pages.OrganizationSettings.auth_credentials", "Credentials (Email/Password)")}
                                    checked={formData.allowed_auth_methods?.includes("CREDENTIALS")}
                                    onChange={() => handleAuthMethodToggle("CREDENTIALS")}
                                    disabled={!isEditing}
                                />
                                <Form.Check
                                    inline
                                    type="checkbox"
                                    id="auth-sso"
                                    label={i18n.t("pages.OrganizationSettings.auth_sso", "Single Sign-On (Keycloak / OIDC)")}
                                    checked={formData.allowed_auth_methods?.includes("SSO")}
                                    onChange={() => handleAuthMethodToggle("SSO")}
                                    disabled={!isEditing}
                                />
                            </Form.Group>

                            {formData.allowed_auth_methods?.includes("SSO") && (
                                <div className="p-3 bg-surface rounded border border-primary-subtle">
                                    <h6 className="mb-3 text-primary">{i18n.t("pages.OrganizationSettings.sso_configuration", "SSO Configuration")}</h6>
                                    <Row>
                                        <Col md={12} className="mb-3">
                                            <Form.Group>
                                                <Form.Label>{i18n.t("pages.OrganizationSettings.sso_issuer_url", "Identity Provider URL")}</Form.Label>
                                                <Form.Control
                                                    type="url"
                                                    placeholder="exemplo: https://keycloak.atlar.pt/realms/atlar"
                                                    value={formData.sso_issuer_url}
                                                    onChange={(e) => handleInputChange("sso_issuer_url", e.target.value)}
                                                    disabled={!isEditing}
                                                />
                                                <Form.Text className="text-muted">
                                                    {i18n.t("pages.OrganizationSettings.sso_issuer_hint", "The base URL of your Identity Provider realm.")}
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <Form.Group>
                                                <Form.Label>{i18n.t("pages.OrganizationSettings.sso_client_id", "Client ID")}</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={formData.sso_client_id}
                                                    onChange={(e) => handleInputChange("sso_client_id", e.target.value)}
                                                    disabled={!isEditing}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <Form.Group>
                                                <Form.Label>{i18n.t("pages.OrganizationSettings.sso_client_secret", "Client Secret")}</Form.Label>
                                                <Form.Control
                                                    type="password"
                                                    value={formData.sso_client_secret}
                                                    placeholder={settings?.has_sso_client_secret ? i18n.t("pages.OrganizationSettings.sso_client_secret_saved") : ""}
                                                    onChange={(e) => handleInputChange("sso_client_secret", e.target.value)}
                                                    disabled={!isEditing}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>
                            )}
                        </Form>
                    </Card.Body>
                </Card>

                <Row className="g-4">
                    <Col md={6}>
                        <Card className="shadow-sm border-0 rounded-4 mb-4 h-100">
                            <Card.Header className="border-bottom bg-body fw-semibold px-4 py-3">{i18n.t("pages.OrganizationSettings.absence_types_title")}</Card.Header>
                            <Card.Body className="px-4 py-3">
                                {isEditing ? (
                                    <>
                                        <div className="d-flex mb-2 gap-2">
                                            <Form.Control type="text" value={absenceTypeInput} onChange={(e) => setAbsenceTypeInput(e.target.value)} placeholder={i18n.t("pages.OrganizationSettings.add_type_placeholder")} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAbsenceType(); } }} />
                                            <Button variant="outline-secondary" size="sm" onClick={addAbsenceType}><BsPlusLg size={14} /></Button>
                                        </div>
                                        <div className="d-flex flex-wrap gap-2">
                                            {formData.default_absence_types.map((type) => (
                                                <span key={type} className="badge bg-light text-dark d-inline-flex align-items-center gap-1 border">
                                                    {DEFAULT_ABSENCE_TYPES.includes(type) ? i18n.t(`pages.OrganizationSettings.absence_type_${type}`) : type}
                                                    <button type="button" className="btn-close btn-sm p-0 ms-1" onClick={() => removeAbsenceType(type)} />
                                                </span>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <div className="d-flex flex-wrap gap-2">
                                        {formData.default_absence_types.map((type) => (
                                            <span key={type} className="badge bg-light text-dark border">
                                                {DEFAULT_ABSENCE_TYPES.includes(type) ? i18n.t(`pages.OrganizationSettings.absence_type_${type}`) : type}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={6}>
                        <Card className="shadow-sm border-0 rounded-4 mb-4 h-100">
                            <Card.Header className="border-bottom bg-body fw-semibold px-4 py-3">{i18n.t("pages.OrganizationSettings.languages_title")}</Card.Header>
                            <Card.Body className="px-4 py-3">
                                {isEditing ? (
                                    <>
                                        <div className="d-flex mb-2 gap-2">
                                            <Form.Control type="text" value={languageInput} onChange={(e) => setLanguageInput(e.target.value)} placeholder={i18n.t("pages.OrganizationSettings.add_language_placeholder")} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLanguage(); } }} />
                                            <Button variant="outline-secondary" size="sm" onClick={addLanguage}><BsPlusLg size={14} /></Button>
                                        </div>
                                        <div className="d-flex flex-wrap gap-2">
                                            {formData.default_languages.map((lang) => (
                                                <span key={lang} className="badge bg-light text-dark d-inline-flex align-items-center gap-1 border">
                                                    {lang.toUpperCase()}
                                                    <button type="button" className="btn-close btn-sm p-0 ms-1" onClick={() => removeLanguage(lang)} />
                                                </span>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <div className="d-flex flex-wrap gap-2">
                                        {formData.default_languages.map((lang) => (
                                            <span key={lang} className="badge bg-light text-dark border">{lang.toUpperCase()}</span>
                                        ))}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card className="shadow-sm border-0 rounded-4 mb-4 h-100">
                            <Card.Header className="border-bottom bg-body fw-semibold px-4 py-3">{i18n.t("pages.OrganizationSettings.max_overtime_per_week")}</Card.Header>
                            <Card.Body className="px-4 py-3 d-flex align-items-center justify-content-center flex-column">
                                <p className="display-6 mb-0 fw-bold text-primary">{formData.max_overtime_hours_per_week}</p>
                                <small className="text-muted">{i18n.t("common.units.hours")}</small>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card className="shadow-sm border-0 rounded-4 mb-4 h-100">
                            <Card.Header className="border-bottom bg-body fw-semibold px-4 py-3">{i18n.t("pages.OrganizationSettings.max_overtime_per_month")}</Card.Header>
                            <Card.Body className="px-4 py-3 d-flex align-items-center justify-content-center flex-column">
                                <p className="display-6 mb-0 fw-bold text-primary">{formData.max_overtime_hours_per_month}</p>
                                <small className="text-muted">{i18n.t("common.units.hours")}</small>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card className="shadow-sm border-0 rounded-4 mb-4 h-100">
                            <Card.Header className="border-bottom bg-body fw-semibold px-4 py-3">{i18n.t("pages.OrganizationSettings.max_overtime_per_year")}</Card.Header>
                            <Card.Body className="px-4 py-3 d-flex align-items-center justify-content-center flex-column">
                                <p className="display-6 mb-0 fw-bold text-primary">{formData.max_overtime_hours_per_year}</p>
                                <small className="text-muted">{i18n.t("common.units.hours")}</small>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card className="shadow-sm border-0 rounded-4 mb-4 h-100">
                            <Card.Header className="border-bottom bg-body fw-semibold px-4 py-3">{i18n.t("pages.OrganizationSettings.overtime_calculation_method")}</Card.Header>
                            <Card.Body className="px-4 py-3 d-flex align-items-center justify-content-center flex-column">
                                <p className="mb-0 fw-bold text-primary">{i18n.t(`pages.OrganizationSettings.calc_method_${formData.overtime_calculation_method}`)}</p>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card className="shadow-sm border-0 rounded-4 mb-4 h-100">
                            <Card.Header className="border-bottom bg-body fw-semibold px-4 py-3">{i18n.t("pages.OrganizationSettings.compensation_type")}</Card.Header>
                            <Card.Body className="px-4 py-3 d-flex align-items-center justify-content-center flex-column">
                                <p className="mb-0 fw-bold text-primary">{i18n.t(`pages.OrganizationSettings.compensation_${formData.compensation_type?.toLowerCase()}`)}</p>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card className="shadow-sm border-0 rounded-4 mb-4 h-100">
                            <Card.Header className="border-bottom bg-body fw-semibold px-4 py-3">{i18n.t("pages.OrganizationSettings.notifications_enabled")}</Card.Header>
                            <Card.Body className="px-4 py-3 d-flex align-items-center justify-content-center flex-column">
                                <p className="mb-0 fw-bold text-primary">{formData.notifications_enabled ? i18n.t("common.actions.yes") : i18n.t("common.actions.no")}</p>
                            </Card.Body>
                        </Card>
                    </Col>

                    {formData.notification_methods.length > 0 && (
                        <Col md={12}>
                            <Card className="shadow-sm border-0 rounded-4 mb-4">
                                <Card.Header className="border-bottom bg-body fw-semibold px-4 py-3">{i18n.t("pages.OrganizationSettings.notification_methods_title")}</Card.Header>
                                <Card.Body className="px-4 py-3">
                                    {isEditing ? (
                                        <>
                                            <div className="d-flex mb-2 gap-2">
                                                <Form.Control type="text" value={notificationMethodInput} onChange={(e) => setNotificationMethodInput(e.target.value)} placeholder={i18n.t("pages.OrganizationSettings.add_notification_method_placeholder")} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNotificationMethod(); } }} />
                                                <Button variant="outline-secondary" size="sm" onClick={addNotificationMethod}><BsPlusLg size={14} /></Button>
                                            </div>
                                            <div className="d-flex flex-wrap gap-2">
                                                {formData.notification_methods.map((method) => (
                                                    <span key={method} className="badge bg-light text-dark d-inline-flex align-items-center gap-1 border">
                                                        {i18n.t(`pages.OrganizationSettings.notification_method_${method}`)}
                                                        <button type="button" className="btn-close btn-sm p-0 ms-1" onClick={() => removeNotificationMethod(method)} />
                                                    </span>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="d-flex flex-wrap gap-2">
                                            {formData.notification_methods.map((method) => (
                                                <span key={method} className="badge bg-light text-dark border">{i18n.t(`pages.OrganizationSettings.notification_method_${method}`)}</span>
                                            ))}
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    )}

                    <Row>
                        <Col md={12}>
                            <Card className="shadow-sm border-0 rounded-4 mb-4">
                                <Card.Header className="border-bottom bg-body fw-semibold px-4 py-3">{i18n.t("pages.OrganizationSettings.tags_title")}</Card.Header>
                                <Card.Body className="px-4 py-3">
                                    {isEditing ? (
                                        <>
                                            <Row>
                                                <Col md={6}>{renderTagsInput("project", i18n.t("pages.OrganizationSettings.project_tags"))}</Col>
                                                <Col md={6}>{renderTagsInput("event", i18n.t("pages.OrganizationSettings.event_tags"))}</Col>
                                            </Row>

                                            <Row>
                                                <Col md={6}>{renderTagsInput("workItem", i18n.t("pages.OrganizationSettings.work_item_tags"))}</Col>
                                                <Col md={6}>{renderTagsInput("document", i18n.t("pages.OrganizationSettings.document_tags"))}</Col>
                                            </Row>

                                            <Row>
                                                <Col md={6} className="mt-3">{renderTagsInput("delegation", i18n.t("pages.OrganizationSettings.delegation_tags"))}</Col>
                                            </Row>

                                            <div className="d-flex gap-2 mt-4">
                                                <Button variant="primary" onClick={handleSave}>{i18n.t("common.actions.save")}</Button>
                                                <Button variant="outline-secondary" onClick={() => setIsEditing(false)}>{i18n.t("common.actions.cancel")}</Button>
                                            </div>
                                        </>
                                    ) : (
                                        <Row className="g-3">
                                            {formData.default_project_tags?.length > 0 && (
                                                <Col md={6}>
                                                    <p className="fw-semibold mb-2">{i18n.t("pages.OrganizationSettings.project_tags")}</p>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {formData.default_project_tags.map((tag) => (<span key={tag} className="badge bg-light text-dark border">{tag}</span>))}
                                                    </div>
                                                </Col>
                                            )}
                                            {formData.default_event_tags?.length > 0 && (
                                                <Col md={6}>
                                                    <p className="fw-semibold mb-2">{i18n.t("pages.OrganizationSettings.event_tags")}</p>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {formData.default_event_tags.map((tag) => (<span key={tag} className="badge bg-light text-dark border">{tag}</span>))}
                                                    </div>
                                                </Col>
                                            )}
                                            {formData.default_work_item_tags?.length > 0 && (
                                                <Col md={6}>
                                                    <p className="fw-semibold mb-2">{i18n.t("pages.OrganizationSettings.work_item_tags")}</p>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {formData.default_work_item_tags.map((tag) => (<span key={tag} className="badge bg-light text-dark border">{tag}</span>))}
                                                    </div>
                                                </Col>
                                            )}
                                            {formData.default_document_tags?.length > 0 && (
                                                <Col md={6}>
                                                    <p className="fw-semibold mb-2">{i18n.t("pages.OrganizationSettings.document_tags")}</p>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {formData.default_document_tags.map((tag) => (<span key={tag} className="badge bg-light text-dark border">{tag}</span>))}
                                                    </div>
                                                </Col>
                                            )}
                                            {formData.default_delegation_tags?.length > 0 && (
                                                <Col md={6}>
                                                    <p className="fw-semibold mb-2">{i18n.t("pages.OrganizationSettings.delegation_tags")}</p>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {formData.default_delegation_tags.map((tag) => (<span key={tag} className="badge bg-light text-dark border">{tag}</span>))}
                                                    </div>
                                                </Col>
                                            )}
                                        </Row>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Row>
                </>
                )}
            </Container>
        </Container>
    );
};

export default OrganizationSettings;